import React, { useState, useMemo } from 'react';
import { hamtaBestallarnamn } from '../utils/nameUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { tillbakaTillKund, navigeraTillbaka } from '../utils/backNavigation';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import Navbar from '../components/common/Navbar';

// Import hooks
import useOffertData from '../features/offert/hooks/useOffertData';
import useOffertFeedback from '../features/offert/hooks/useOffertFeedback';
import useOffertActions from '../features/offert/hooks/useOffertActions';
import useOffertCustomerResponse from '../features/offert/hooks/useOffertCustomerResponse';

// Import komponenter
import LoadingErrorStates from '../features/offert/components/LoadingErrorStates';
import HeaderSection from '../features/offert/components/sections/HeaderSection';
import StatusMessages from '../features/offert/components/sections/StatusMessages';
import { arExtra, visaBeteckning } from '../utils/offertSpar';
import ProjectInfoEditor from '../features/offert/components/sections/ProjectInfoEditor';
import WorkDescriptionSection from '../features/offert/components/sections/WorkDescriptionSection';
import RoomsSection from '../features/offert/components/sections/RoomsSection';
import BuilderSection from '../features/offert/components/sections/BuilderSection';
import CustomerResponseSection from '../features/offert/components/sections/CustomerResponseSection';
import TermsSection from '../features/offert/components/sections/TermsSection';
import PaymentPlanSection from '../features/offert/components/sections/PaymentPlanSection';
import PricingSection from '../features/offert/components/sections/PricingSection';
import { ThreadFeedbackSection } from '../features/shared/feedback';
import SendOffertModal from '../features/offert/components/SendOffertModal';
import AcceptOnBehalfConfirmModal from '../features/offert/components/AcceptOnBehalfConfirmModal';
import ChangeSummarySection from '../features/offert/components/sections/ChangeSummarySection';
import PageMobileFooter from '../components/PageMobileFooter';
import ProofreadModal from '../features/estimat/components/ProofreadModal';

/**
 * Offert - Konsoliderad komponent för både konsult- och kundvy
 * 
 * Rollbaserad rendering:
 * - Konsult (byggkonsult/admin): Kan skicka offert, redigera giltighetstid och projektinfo
 * - Kund (customer/external): Readonly presentation med möjlighet att ge feedback
 * 
 * Routing:
 * - /offert/:offertId → Konsultvy
 * - /offert/view/:offertId → Kundvy
 * 
 * Props:
 * - embedded: Om true, renderas i en modal (används i MyProjects.js)
 * - offertIdOverride: Tvingar en specifik offert-ID
 * - comparisonMode: Om true, används i jämförelsevy
 * - onCloseAfterSuccess: Callback som anropas när success modal stängs efter en framgångsrik action
 */
const Offert = ({ embedded = false, offertIdOverride, comparisonMode = false, onCloseAfterSuccess, showYrkesgruppColorsOverride, showSectionCostsOverride: showSectionCostsProp, hiddenSections = [] }) => {
  const { offertId } = useParams();
  const oid = offertIdOverride || offertId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();

  // 🎯 Bestäm vy baserat på user role. I jämförelseläge (comparisonMode) visas
  // alltid den rena kundvyn — även för BK — så jämförelsen blir read-only och
  // identisk med kundens jämför-funktion (inga redigerings-/skicka-knappar).
  const isConsultantView = !comparisonMode && (user?.role === 'byggkonsult' || user?.role === 'admin');
  const isCustomerView = !isConsultantView;

  // 🎯 Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    feedback: true,
    byggforetag: true,
    prisuppgifter: true,
    projektstart: true,
    berordarum: true,
    arbetsbeskrivning: true,
    betalplan: true,
    villkor: true // Expanderad som default så att kunder ser villkoren direkt
  });

  // 🎨 Yrkesgrupp-färgkodning
  // Override-prop tar företräde (används t.ex. av byggare i workspace).
  // Annars: lokal state initialiserad från localStorage. BK + IA kan toggla
  // direkt i offert-sidan (kund ser ingen toggle).
  const [localShowYrkesgruppColors, setLocalShowYrkesgruppColors] = useState(
    // Standard AV — offerten färgkodas bara när någon aktivt slår på det.
    // _v2 så att gamla sparade "true" från förra standarden inte hänger kvar.
    () => localStorage.getItem('offert_showYrkesgruppColors_v2') === 'true'
  );
  const showYrkesgruppColors = showYrkesgruppColorsOverride !== undefined
    ? showYrkesgruppColorsOverride
    : localShowYrkesgruppColors;
  // Toggle visas för ALLA utom kund/extrakund (BK/IA/admin/byggare/UE/projektör)
  // Kunder ser aldrig färgkodning som konfigurerbar — den styrs då helt av kalkyl.
  const canToggleColors =
    showYrkesgruppColorsOverride === undefined &&
    !['kund', 'extrakund'].includes(user?.role);
  const handleToggleYrkesgruppColors = (next) => {
    setLocalShowYrkesgruppColors(next);
    localStorage.setItem('offert_showYrkesgruppColors_v2', String(next));
  };

  // 🎯 Sektionspriser - state initialiseras från offert.puData när offert laddas
  const [showSectionCosts, setShowSectionCosts] = useState(false);
  const [savingSectionCosts, setSavingSectionCosts] = useState(false);

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // 🎯 Modal state
  const [showResultModal, setShowResultModal] = useState(false);
  const [modalData, setModalData] = useState({ title: '', message: '', type: 'success' });
  const [showSendModal, setShowSendModal] = useState(false);

  // 🎯 PDF export state
  const [exportingPdf, setExportingPdf] = useState(false);

  // 🎯 Godkänn åt kund (BK accepterar offert åt kunden)
  const [acceptingOnBehalf, setAcceptingOnBehalf] = useState(false);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);

  const showModal = (data) => {
    setModalData(data);
    setShowResultModal(true);
  };

  // 🎯 Exportera Offert-PDF till SharePoint (bygger riktig PDF med html2canvas + jsPDF)
  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);

    try {
      const { generateOffertPdf } = await import('../features/offert/utils/generateOffertPdf');

      const pdfBlob = await generateOffertPdf({ offert, procurement });

      // Ladda upp till SharePoint
      const formData = new FormData();
      formData.append('file', pdfBlob, 'offert.pdf');

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/offert/${oid}/export-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Kunde inte exportera offert-PDF');
      }

      showSuccess(
        'Sparad till SharePoint',
        `Filen "${data.fileName}" har sparats till kundens Avtal-mapp på SharePoint.`,
        {
          actions: [
            {
              label: 'Öppna filen',
              icon: 'M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25',
              onClick: () => window.open(data.sharepointUrl, '_blank')
            }
          ]
        }
      );
    } catch (err) {
      console.error('[Offert PDF] FEL:', err);
      showError('Exportfel', err.message || 'Kunde inte exportera offert-PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // 🎯 BK godkänner offerten ÅT kunden (t.ex. kunden ringde och accepterade)
  const confirmAcceptOnBehalf = async () => {
    if (acceptingOnBehalf) return;
    setAcceptingOnBehalf(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/offert/${oid}/accept-on-behalf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Kunde inte godkänna offerten');
      }
      setShowAcceptConfirm(false);
      await fetchOffertData();
      showSuccess('Offert godkänd', 'Offerten har godkänts åt kunden.');
    } catch (err) {
      showError('Kunde inte godkänna', err.message || 'Ett fel uppstod');
    } finally {
      setAcceptingOnBehalf(false);
    }
  };

  // 🎯 Hooks - Data management
  const {
    offert,
    procurement,
    feedbackThread,
    feedbackHistory,
    loading,
    error,
    setOffert,
    setFeedbackThread,
    fetchOffertData,
    fetchFeedbackThread,
    fetchFeedbackHistory,
    fetchIntelligentStatus,
  } = useOffertData(oid);

  // 🎯 Hooks - Feedback management
  const feedbackHook = useOffertFeedback(
    offert,
    setFeedbackThread,
    fetchFeedbackThread,
    fetchFeedbackHistory,
    fetchIntelligentStatus,
    showModal,
    showModal, // onError uses same function
    isConsultantView ? 'consultant' : 'customer' // viewMode
  );

  // Destructure för enklare åtkomst
  const {
    expandedFeedbackItems,
    setExpandedFeedbackItems,
    newReplies,
    setNewReplies,
    savedReplies,
    setSavedReplies,
    handleSaveReply,
    handleSendAllReplies,
    handleMarkAsOK,
    sendingResponse
  } = feedbackHook;

  // Korrekturläs av sparade feedback-svar (endast för byggkonsult)
  const [showProofreadFeedback, setShowProofreadFeedback] = useState(false);
  const feedbackProofreadFields = useMemo(() => {
    const fields = [];
    Object.entries(savedReplies || {}).forEach(([key, value]) => {
      if (typeof value !== 'string' || !value.trim()) return;
      fields.push({
        id: `offert-feedback-${key}`,
        label: 'Feedback-svar',
        value,
        onApply: (newValue) => {
          setSavedReplies(prev => ({ ...prev, [key]: newValue }));
          setNewReplies(prev => ({ ...prev, [key]: newValue }));
        }
      });
    });
    return fields;
  }, [savedReplies, setSavedReplies, setNewReplies]);

  // 🎯 Hooks - Offert actions (bara för konsulter)
  const { sending, handleSendOffert } = useOffertActions(
    offert,
    procurement,
    setOffert,
    showModal,
    showModal // onError uses same function
  );

  // 🎯 Hooks - Customer response (bara för kunder)
  const isNewerVersion = offert?.version && parseFloat(offert.version) > 1.0;
  const customerResponseHook = useOffertCustomerResponse(
    offert,
    setOffert,
    fetchFeedbackThread,
    fetchIntelligentStatus,
    showModal,
    showModal, // onError uses same function
    isNewerVersion
  );

  const {
    response,
    comment,
    submitting,
    canRespond,
    hasResponded,
    setResponse,
    setComment,
    handleSubmitResponse,
  } = customerResponseHook;

  // 🎯 Synkronisera showSectionCosts från offert.puData
  React.useEffect(() => {
    if (offert?.puData?.workDescription) {
      const puDataWrapper = offert.puData.workDescription;
      let initialValue = false;

      if (puDataWrapper?.excelData?.sheets?.[0]?.showSectionCosts !== undefined) {
        initialValue = puDataWrapper.excelData.sheets[0].showSectionCosts;
      } else if (puDataWrapper?.sheets?.[0]?.showSectionCosts !== undefined) {
        initialValue = puDataWrapper.sheets[0].showSectionCosts;
      }

      setShowSectionCosts(initialValue);
    }
  }, [offert?.puData?.workDescription]);

  // 🎯 Handler för att toggla sektionspriser
  const handleToggleSectionCosts = async (newValue) => {
    if (savingSectionCosts) return;

    setSavingSectionCosts(true);
    const previousValue = showSectionCosts;
    setShowSectionCosts(newValue); // Optimistic update

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_URL}/api/offert/${oid}/display-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ showSectionCosts: newValue })
      });

      if (!response.ok) {
        throw new Error('Kunde inte spara inställning');
      }

      // Refetcha så sectionCosts räknas om från kalkyl (getOffert bygger om puData)
      await fetchOffertData();
    } catch (error) {
      console.error('Fel vid sparning av sektionspriser:', error);
      setShowSectionCosts(previousValue); // Rollback on error
    } finally {
      setSavingSectionCosts(false);
    }
  };

  // 🎯 Bestäm navigation destination baserat på roll och projektstatus
  const getBackDestination = () => {
    if (embedded) return null;
    if (user?.role === 'inredningsarkitekt') {
      return '/workspace/projekt';
    }
    // Tillbaka ska landa på kunden man kom ifrån, inte högst upp i listan.
    // tillbakaTillKund väljer flik utifrån om upphandlingen blivit projekt.
    if (isConsultantView) {
      return tillbakaTillKund(procurement);
    }
    // Kunden till sin egen startsida. '/dashboard' är adminsidan System —
    // dit skickades kunden tidigare och kraschade på projekthämtningen, som
    // kräver admin.
    if (user?.role === 'kund') {
      return '/myprojects';
    }
    return '/dashboard';
  };

  // 🎯 Hantera loading, error och not found states
  const loadingOrError = (
    <LoadingErrorStates
      user={user}
      loading={loading}
      error={error}
      notFound={!offert || !procurement}
      embedded={embedded}
      getBackDestination={getBackDestination}
    />
  );

  if (!user || loading || error || !offert || !procurement) {
    return loadingOrError;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {!embedded && <Navbar currentUser={user} />}

      <div className="max-w-screen-2xl mx-auto py-6 px-4 lg:px-6">
        {/* Header med kompakt översikt */}
        <HeaderSection
          offert={offert}
          procurement={procurement}
          user={user}
          isConsultantView={isConsultantView}
          isCustomerView={isCustomerView}
          embedded={embedded}
          sending={sending}
          onSendOffert={() => setShowSendModal(true)}
          onValidityUpdate={(newValidityDays) => {
            setOffert(prev => ({ ...prev, validityDays: newValidityDays }));
          }}
          getBackDestination={getBackDestination}
          onExportPdf={handleExportPdf}
          exportingPdf={exportingPdf}
          /**
           * "Godkänn åt kund" döljs bara när en NYARE version i samma spår
           * redan är godkänd — inom ett spår gäller den senaste godkända.
           * Backend räknar ut det (`nyareVersionGodkand`) med samma regel som
           * själva godkännandet. `procurement.selectedOffert` dög inte: den
           * står kvar på den gamla versionen när en ny skickats och dolde då
           * knappen på den nya.
           */
          onAcceptOnBehalf={offert?.nyareVersionGodkand
            ? undefined
            : () => setShowAcceptConfirm(true)}
          acceptingOnBehalf={acceptingOnBehalf}
        />

        {/* Tillägg under produktion — kunden måste förstå att den godkända
            offerten står kvar, annars läses det här som en ersättare. */}
        {arExtra(offert) && (
          <div className="mb-4 rounded-lg border-l-4 border-amber-400 bg-amber-50 p-4">
            <h2 className="text-sm font-semibold text-amber-900">
              {visaBeteckning(offert)} — tillägg till den godkända offerten
            </h2>
            <p className="mt-1 text-sm text-amber-800">
              Den här offerten gäller tillkommande arbete under produktion. Din tidigare
              godkända offert gäller fortsatt och påverkas inte av vad du svarar här.
            </p>
          </div>
        )}

        {/* Status meddelanden */}
        {!hiddenSections.includes('status') && (
          <StatusMessages
            offert={offert}
            isConsultantView={isConsultantView}
            isCustomerView={isCustomerView}
            hasResponded={hasResponded}
          />
        )}

        {/* Feedback-sektion - visa för konsulter alltid, för kunder bara om det finns feedback.
            Döljs i jämförelseläge (read-only sida-vid-sida). */}
        {!comparisonMode && !hiddenSections.includes('feedback') && (isConsultantView || offert.customerComment || (feedbackHistory && feedbackHistory.length > 0)) && (
          <ThreadFeedbackSection
            title={isCustomerView ? "Din feedback" : "Feedback"}
            amne="offerten"
            feedbackThread={feedbackThread}
            feedbackHistory={feedbackHistory}
            currentVersion={offert?.version}
            expandedFeedbackItems={expandedFeedbackItems}
            setExpandedFeedbackItems={setExpandedFeedbackItems}
            newReplies={newReplies}
            savedReplies={savedReplies}
            onReplyChange={(key, value) => setNewReplies(prev => ({ ...prev, [key]: value }))}
            onReplySave={handleSaveReply}
            onMarkAsOK={handleMarkAsOK}
            onSendAllReplies={handleSendAllReplies}
            sendingResponse={sendingResponse}
            procurement={procurement}
            user={user}
            viewMode={isConsultantView ? "consultant" : "customer"}
            feedbackKey="offert_feedback"
            onProofread={isConsultantView ? () => setShowProofreadFeedback(true) : undefined}
          />
        )}

        {/* Underlag visas inte här.
            Byggkonsulten når skissen via estimatvyn, där SkissSection hör hemma,
            och kunden har sin egen Underlag-sektion i MyProjects. Kopian i
            offerten var bara BK-synlig och gav ingenting utöver estimatet. */}

        {/* Korrekturläs-modal för feedback-svar (offert) */}
        <ProofreadModal
          isOpen={showProofreadFeedback}
          onClose={() => setShowProofreadFeedback(false)}
          fields={feedbackProofreadFields}
        />

        {/* Ändringssammanfattning - Visa för kunder vid v1.1+ */}
        {offert.changeSummary && (
          <ChangeSummarySection changeSummary={offert.changeSummary} version={offert.version} />
        )}

        {/* Svara på offert - Endast för kunder som kan svara (ej för byggare eller godkända offerter).
            Döljs i jämförelseläge så man inte kan svara/acceptera från en sida-vid-sida-jämförelse. */}
        {!comparisonMode && !hiddenSections.includes('customerResponse') && isCustomerView && user?.role !== 'byggare' && canRespond && !hasResponded &&
         offert.status !== 'accepted' && offert.previousStatus !== 'accepted' && (
          <CustomerResponseSection
            response={response}
            comment={comment}
            submitting={submitting}
            feedbackThread={feedbackThread}
            onResponseChange={setResponse}
            onCommentChange={setComment}
            onSubmit={handleSubmitResponse}
            isNewerVersion={isNewerVersion}
            // Spåret styr texterna: ett tillägg godkänns vid sidan av den redan
            // godkända offerten, så "Boka byggare" och löftet om att starta
            // projekteringen gäller inte — det är redan gjort.
            tillaggsNummer={offert.extraNummer ?? null}
          />
        )}

        {/* Byggföretag */}
        {!hiddenSections.includes('byggforetag') && (
          <BuilderSection
            offert={offert}
            isExpanded={expandedSections.byggforetag}
            onToggle={() => toggleSection('byggforetag')}
            viewMode={isConsultantView ? "consultant" : "customer"}
          />
        )}

        {/* Prisuppgifter */}
        {!hiddenSections.includes('prisuppgifter') && (
          <PricingSection
            offert={offert}
            procurement={procurement}
            isExpanded={expandedSections.prisuppgifter}
            onToggle={() => toggleSection('prisuppgifter')}
          />
        )}

        {/* Planerad projektstart - Redigerbar för konsulter, readonly för kunder */}
        {isConsultantView ? (
          <ProjectInfoEditor
            offert={offert}
            procurement={procurement}
            user={user}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
          />
        ) : (
          // Readonly version för kunder
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <button
              onClick={() => toggleSection('projektstart')}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-leadme-gray-dark">Planerad projektstart</h2>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${
                  expandedSections.projektstart ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSections.projektstart && (() => {
              // Hämta från projektet (exakta datum), annars fallback till offertens textfält
              const rawStartDate = offert?.projectStartDate || offert?.projectInfo?.startDate || offert?.puData?.projectInfo?.startDate;
              const rawEndDate = offert?.projectEndDate;
              const workDays = offert?.projectWorkDays || offert?.projectInfo?.workDays || offert?.puData?.projectInfo?.workDays;
              const formatDateVal = (date) => {
                if (!date) return null;
                const str = String(date);
                if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
                  const d = new Date(str);
                  if (!isNaN(d.getTime())) {
                    return d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
                  }
                }
                return str;
              };
              const startDate = formatDateVal(rawStartDate);
              const endDate = formatDateVal(rawEndDate);

              return (
                <div className="px-6 pb-6">
                  <p className="text-sm text-gray-600 mb-4">Detta är den planerade tidsramen för ert projekt baserat på era önskemål och vår planering.</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {startDate && (
                        <div>
                          <p className="text-sm font-medium text-green-700">Startdatum</p>
                          <p className="text-base text-gray-700">{startDate}</p>
                        </div>
                      )}

                      {endDate && (
                        <div>
                          <p className="text-sm font-medium text-green-700">Estimerat slutdatum</p>
                          <p className="text-base text-gray-700">{endDate}</p>
                        </div>
                      )}

                      {workDays && (
                        <div>
                          <p className="text-sm font-medium text-green-700">Antal arbetsdagar</p>
                          <p className="text-base text-gray-700">{workDays} dagar</p>
                        </div>
                      )}
                    </div>

                    {!startDate && !workDays && (
                      <p className="text-sm text-gray-600 italic">Projektstart ej angiven ännu</p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Berörda rum */}
        <RoomsSection
          offert={offert}
          isExpanded={expandedSections.berordarum}
          onToggle={() => toggleSection('berordarum')}
        />

        {/* Arbetsbeskrivning */}
        <WorkDescriptionSection
          offert={offert}
          isExpanded={expandedSections.arbetsbeskrivning}
          onToggle={() => toggleSection('arbetsbeskrivning')}
          showYrkesgruppColors={showYrkesgruppColors}
          onToggleYrkesgruppColors={canToggleColors ? handleToggleYrkesgruppColors : null}
          canFilterByYrkesgrupp={!['kund', 'extrakund'].includes(user?.role)}
          showSectionCostsOverride={showSectionCostsProp !== undefined ? showSectionCostsProp : showSectionCosts}
          onToggleSectionCosts={isConsultantView ? handleToggleSectionCosts : null}
          savingSectionCosts={savingSectionCosts}
        />

        {/* Betalplan-sektion — dölj för UE och projektörer */}
        {(offert.paymentPlan || offert.puData?.paymentPlan) && !['UE', 'projektor'].includes(user?.role) && !hiddenSections.includes('betalplan') && (
          <PaymentPlanSection
            paymentPlan={offert.paymentPlan || offert.puData?.paymentPlan}
            isExpanded={expandedSections.betalplan}
            onToggle={() => toggleSection('betalplan')}
          />
        )}

        {/* Villkor-sektion */}
        {!hiddenSections.includes('villkor') && (
          <TermsSection
            offert={offert}
            procurement={procurement}
            isExpanded={expandedSections.villkor}
            onToggle={() => toggleSection('villkor')}
          />
        )}

        {/* PDF Export overlay */}
        {exportingPdf && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl px-8 py-6 flex items-center space-x-4">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-leadme-pink"></div>
              <span className="text-gray-800 font-medium">Exporterar offert-PDF...</span>
            </div>
          </div>
        )}

        {/* Send Offert Modal */}
        <SendOffertModal
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
          onConfirm={(changeSummary, customerMessage) => {
            setShowSendModal(false);
            handleSendOffert(changeSummary, customerMessage);
          }}
          version={offert.version}
          sending={sending}
          defaultChangeSummary={offert.puThreadId?.changeSummary || ''}
          offertMailSent={Boolean(procurement?.offertMailSent)}
          customerFirstName={procurement?.customerId?.firstName || ''}
          bkFirstName={user?.firstName || ''}
          offertId={offert._id}
          skickarInloggningsuppgifter={Boolean(
            procurement?.estimatSkipped && !procurement?.inloggningsuppgifterSkickadeAt
          )}
        />

        {/* Godkänn åt kund-bekräftelse (BK) */}
        <AcceptOnBehalfConfirmModal
          isOpen={showAcceptConfirm}
          onClose={() => setShowAcceptConfirm(false)}
          onConfirm={confirmAcceptOnBehalf}
          loading={acceptingOnBehalf}
          customerName={hamtaBestallarnamn(procurement?.customerId) || ''}
        />

        {/* Result Modal */}
        {showResultModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div 
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                onClick={() => setShowResultModal(false)}
              />

              {/* Modal panel */}
              <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                {/* Icon */}
                <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 ${modalData.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                  <svg className={`h-8 w-8 ${modalData.type === 'success' ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {modalData.type === 'success' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    )}
                  </svg>
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{modalData.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-8">{modalData.message}</p>
                </div>

                {/* Action buttons */}
                <div className="flex justify-center space-x-3">
                  {modalData.type === 'success' && !embedded && isConsultantView && (
                    <button
                      onClick={() => {
                        setShowResultModal(false);
                        navigeraTillbaka(navigate, getBackDestination());
                      }}
                      className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    >
                      Tillbaka till projekt
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowResultModal(false);
                      if (embedded && onCloseAfterSuccess) {
                        setTimeout(() => onCloseAfterSuccess(), 100);
                      }
                    }}
                    className="px-6 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark transition-colors font-medium"
                  >
                    {modalData.type === 'success' && isConsultantView ? 'Stanna kvar' : 'OK'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!embedded && <PageMobileFooter onRefresh={() => window.location.reload()} />}
      </div>
    </div>
  );
};

export default Offert;

